import {
  createContext,
  type PropsWithChildren,
  useContext,
  useMemo,
} from "react";

import {
  useOptionalWorkspaceRepository,
  useWorkspaceRepository,
} from "./WorkspaceRepository.context";
import {
  useWorkspaceSelectionController,
  type WorkspaceSelectionBrowser,
  type WorkspaceSelectionController,
} from "./workspaceSelection.controller";

const WorkspaceSelectionContext =
  createContext<WorkspaceSelectionController | null>(null);

export interface WorkspaceSelectionProviderProps extends PropsWithChildren {
  browser?: WorkspaceSelectionBrowser;
}

const WorkspaceSelectionProviderConnected = ({
  browser,
  children,
}: WorkspaceSelectionProviderProps) => {
  const { document } = useWorkspaceRepository();
  const workspaceIds = useMemo(
    () => document.workspaces.map((workspace) => workspace.id),
    [document.workspaces],
  );
  const value = useWorkspaceSelectionController(
    workspaceIds,
    browser ?? window,
  );

  return (
    <WorkspaceSelectionContext.Provider value={value}>
      {children}
    </WorkspaceSelectionContext.Provider>
  );
};

export const WorkspaceSelectionProvider = ({
  browser,
  children,
}: WorkspaceSelectionProviderProps) => {
  const repository = useOptionalWorkspaceRepository();
  if (!repository) {
    return null;
  }

  return (
    <WorkspaceSelectionProviderConnected browser={browser}>
      {children}
    </WorkspaceSelectionProviderConnected>
  );
};

export const useWorkspaceSelection = (): WorkspaceSelectionController => {
  const value = useContext(WorkspaceSelectionContext);
  if (!value) {
    throw new Error(
      "useWorkspaceSelection must be used within WorkspaceSelectionProvider",
    );
  }
  return value;
};
